import { useState, useEffect, type ReactElement } from 'react';
import clsx from 'clsx';
import styles from './style.module.scss';
import { clamp } from '@/utils/clamp';

interface ProgressBarProperties {
  readonly className?: string;
  readonly progressPercent: number;
}

const SEGMENT_COUNT = 4;
const SEGMENT_WIDTH = 100 / SEGMENT_COUNT;

const ProgressBar = ({ className, progressPercent }: ProgressBarProperties): ReactElement => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const clamped = clamp(progressPercent, 0, 100);

    requestAnimationFrame(() => {
      setWidth(clamped);
    });
  }, [progressPercent]);

  return (
    <div
      className={clsx(styles['progress-bar'], className)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={width}
    >
      {Array.from({ length: SEGMENT_COUNT }, (_, index) => {
        const isFinalSegment = index === SEGMENT_COUNT - 1;
        const segmentStart = index * SEGMENT_WIDTH;
        const segmentProgress = clamp((width - segmentStart) / SEGMENT_WIDTH, 0, 1);

        return (
          <div
            key={index}
            className={styles.segment}
            data-testid={`progress-bar-segment-${index + 1}`}
            data-final-segment={isFinalSegment}
          >
            <div
              className={clsx(styles.fill, { [styles['fill-final']]: isFinalSegment })}
              /* eslint-disable-next-line react/forbid-dom-props -- width is derived per segment from animated progress state */
              style={{ width: `${segmentProgress * 100}%` }}
              data-testid={`progress-bar-fill-${index + 1}`}
            />
          </div>
        );
      })}
    </div>
  );
};

export default ProgressBar;
